import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import { useBusiness } from '../context/BusinessContext';
import { api } from '../api/client';

const TYPES = [
  { value: 'income', label: 'Income' },
  { value: 'expense', label: 'Expense' },
  { value: 'asset', label: 'Asset' },
  { value: 'liability', label: 'Liability' },
  { value: 'equity', label: 'Equity' },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.value, t.label]));
const EMPTY = { type: 'expense', name: '', code: '', is_bank: false, is_active: true };

const FILTERS = [{ key: 'all', label: 'All' }, ...TYPES.map((t) => ({ key: t.value, label: t.label }))];

export default function Accounts() {
  const { activeId } = useBusiness();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // IRS mileage rate (global setting)
  const [mileageRate, setMileageRate] = useState(null);
  const [rateInput, setRateInput] = useState('');
  const [rateEditing, setRateEditing] = useState(false);
  const [rateSaving, setRateSaving] = useState(false);
  const [rateMsg, setRateMsg] = useState(null);

  useEffect(() => {
    api('/expenses/settings').then((s) => { if (s?.mileage_rate != null) setMileageRate(Number(s.mileage_rate)); }).catch(() => {});
  }, []);

  async function saveRate() {
    const v = Number(rateInput);
    if (Number.isNaN(v) || v <= 0 || v > 5) { setRateMsg({ ok: false, text: 'Enter dollars per mile, e.g. 0.725' }); return; }
    setRateSaving(true); setRateMsg(null);
    try {
      const r = await api('/expenses/settings', { method: 'PUT', body: { mileage_rate: v } });
      setMileageRate(Number(r.mileage_rate)); setRateEditing(false);
      setRateMsg({ ok: true, text: 'Mileage rate updated.' });
    } catch (e) { setRateMsg({ ok: false, text: e?.detail || 'Could not update the rate.' }); }
    finally { setRateSaving(false); }
  }

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api(`/accounts?business_id=${activeId}&all=1`)
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => { setRows([]); if (e?.status && e.status !== 404) setError('Could not load accounts.'); })
      .finally(() => setLoading(false));
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => rows.filter((r) =>
    (filter === 'all' || r.type === filter) && (showInactive || r.is_active !== false)
  ), [rows, filter, showInactive]);

  function openNew() {
    setEditingId(null);
    setForm({ ...EMPTY, type: filter !== 'all' ? filter : 'expense' });
    setFormError(null); setOpen(true);
  }
  function openEdit(row) {
    setEditingId(row.id);
    setForm({ type: row.type, name: row.name || '', code: row.code || '', is_bank: !!row.is_bank, is_active: row.is_active !== false });
    setFormError(null); setOpen(true);
  }
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    if (!form.name.trim()) { setFormError('Name is required.'); return; }
    setSaving(true); setFormError(null);
    try {
      if (editingId) {
        await api(`/accounts/${editingId}`, { method: 'PUT', body: {
          business_id: activeId, name: form.name.trim(), code: form.code.trim() || undefined,
          is_bank: form.is_bank, is_active: form.is_active,
        } });
      } else {
        await api('/accounts', { method: 'POST', body: {
          business_id: activeId, type: form.type, name: form.name.trim(),
          code: form.code.trim() || undefined, is_bank: form.is_bank,
        } });
      }
      setOpen(false); load();
    } catch (e) {
      setFormError(e?.detail || 'Could not save the account.');
    } finally { setSaving(false); }
  }

  const columns = [
    { key: 'code', header: 'Code', render: (r) => <span className="num">{r.code}</span> },
    { key: 'name', header: 'Name' },
    { key: 'type', header: 'Type', render: (r) => TYPE_LABEL[r.type] || r.type },
    { key: 'flags', header: '', render: (r) => (r.is_bank ? <span className="pill pill--noncontract">Bank</span> : '') },
    { key: 'is_active', header: 'Status', render: (r) => <StatusBadge status={r.is_active === false ? 'cancelled' : 'active'} /> },
  ];

  return (
    <>
      <PageHeader title="Chart of accounts" actions={<button className="btn btn--primary" onClick={openNew}>New account</button>} />

      <section className="panel rate-card">
        <div className="rate-card__row">
          <div>
            <div className="rate-card__label">IRS standard mileage rate</div>
            {!rateEditing ? (
              <div className="rate-card__value">{mileageRate != null ? `$${mileageRate.toFixed(3)} / mile` : '—'}</div>
            ) : (
              <div className="rate-card__edit">
                <span>$</span>
                <input type="number" step="0.001" min="0" value={rateInput} autoFocus
                  onChange={(e) => setRateInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveRate(); }} />
                <span>/ mile</span>
              </div>
            )}
            <div className="hint">Used to calculate mileage expenses. Update it when the IRS publishes a new annual rate.</div>
            {rateMsg && <div className={rateMsg.ok ? 'form-note' : 'form-error'} style={{ marginTop: 6 }}>{rateMsg.text}</div>}
          </div>
          <div className="rate-card__actions">
            {!rateEditing ? (
              <button className="btn btn--sm" onClick={() => { setRateInput(mileageRate != null ? String(mileageRate) : ''); setRateMsg(null); setRateEditing(true); }}>Edit rate</button>
            ) : (
              <>
                <button className="btn btn--sm" onClick={() => { setRateEditing(false); setRateMsg(null); }} disabled={rateSaving}>Cancel</button>
                <button className="btn btn--sm btn--primary" onClick={saveRate} disabled={rateSaving}>{rateSaving ? 'Saving…' : 'Save'}</button>
              </>
            )}
          </div>
        </div>
      </section>

      <div className="filter-chips">
        {FILTERS.map((f) => (
          <button key={f.key} className={'chip' + (filter === f.key ? ' chip--on' : '')} onClick={() => setFilter(f.key)}>{f.label}</button>
        ))}
        <label className="check" style={{ marginLeft: "auto" }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive
        </label>
      </div>

      <section className="panel">
        <DataTable
          columns={columns}
          rows={visible}
          loading={loading}
          error={error}
          getRowKey={(r) => r.id}
          onRowClick={openEdit}
          empty="No accounts here yet. Use New account to add income, expense, bank, or credit-card accounts."
        />
      </section>

      <Drawer
        open={open}
        title={editingId ? 'Edit account' : 'New account'}
        onClose={() => !saving && setOpen(false)}
        footer={<>
          <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
          <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : editingId ? 'Save changes' : 'Create account'}</button>
        </>}
      >
        {formError && <div className="form-error">{formError}</div>}

        <div className="field">
          <label>Type</label>
          <select value={form.type} disabled={!!editingId} onChange={(e) => set('type', e.target.value)}>
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {editingId && <p className="hint">Type can't be changed after an account is created.</p>}
          {!editingId && form.type === 'liability' && <p className="hint">Use Liability for credit cards and lines of credit — they'll appear under "Paid from" on expenses.</p>}
        </div>

        <div className="field">
          <label>Name</label>
          <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder={form.type === 'expense' ? 'e.g. Travel & Airfare' : form.type === 'income' ? 'e.g. Simulator Memberships' : 'e.g. Business Credit Card'} autoFocus />
        </div>

        <div className="field">
          <label>Code <span className="hint">(optional)</span></label>
          <input value={form.code} onChange={(e) => set('code', e.target.value)} placeholder="Auto-assigned if left blank" />
        </div>

        {form.type === 'asset' && (
          <label className="check">
            <input type="checkbox" checked={form.is_bank} onChange={(e) => set('is_bank', e.target.checked)} />
            This is a bank account (can be reconciled and selected as "Paid from")
          </label>
        )}

        {editingId && (
          <label className="check">
            <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
            Active (uncheck to hide from pickers without deleting history)
          </label>
        )}
      </Drawer>
    </>
  );
}
